import React, { useState, useEffect } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { lightColors } from '../theme/tokens/colors'; // Explicitly use light theme for Login
import {
  Button,
  TextField,
  Typography,
  Container,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  GlobalStyles
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ErrorOutline as ErrorIcon,
  CheckCircle
} from '@mui/icons-material';
import GenericToaster, { showSuccessToast, showErrorToast } from '../theme/create_toast/GenericToaster';
import Store from '../index';
// Use lightColors for static styling
const colors = lightColors;

// Styled Components
const LoginContainerBox = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#ffffff !important',
  padding: theme.spacing(2),
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56, // Fixed height to match width
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.palette.primary.main,
  borderRadius: 12,
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
}));

const LoginCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  borderRadius: 16,
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.04)',
  border: 'none',
  overflow: 'visible',
  backgroundColor: '#ffffff !important',
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(4, 3),
  '&:last-child': {
    paddingBottom: theme.spacing(4),
  },
}));

const Form = styled('form')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#333333',
  marginBottom: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    '& fieldset': {
      borderColor: colors.palette.grey.border,
    },
    '&:hover fieldset': {
      borderColor: colors.palette.grey.borderDark,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '16px 14px 16px 40px',
    color: '#333333 !important',
    '&:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 1000px #ffffff inset !important`,
      WebkitTextFillColor: `#333333 !important`,
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(0, 0, 0, 0.4) !important',
    opacity: 1,
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  backgroundColor: colors.palette.primary.main,
  color: colors.palette.primary.contrastText,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '1.1rem',
  height: '56px',
  borderRadius: 12,
  boxShadow: 'none',
  marginTop: theme.spacing(2),
  '&:hover': {
    backgroundColor: colors.palette.primary.dark,
    boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
  },
}));

const SuccessMessageData = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(colors.palette.status.success, 0.1),
  border: `1px solid ${alpha(colors.palette.status.success, 0.2)}`,
  borderRadius: 8,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  color: colors.palette.status.success,
  fontSize: '0.875rem',
}));

const ErrorContainerData = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(colors.palette.status.error, 0.1),
  border: `1px solid ${alpha(colors.palette.status.error, 0.2)}`,
  color: colors.palette.status.error,
  borderRadius: 8,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.875rem',
}));

const Footer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  textAlign: 'center',
}));

const CustomLogin = ({ onLoginSuccess, companyName, appType, instanceId }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [realm, setRealm] = useState(companyName || 'testingapplogin');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientId, setClientId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  // Construct the client ID from appType and instanceId
  useEffect(() => {
    if (appType && instanceId) {
      const constructedClientId = `${appType}${instanceId}`;
      setClientId(constructedClientId);
      console.log('Login component initialized with:', {
        companyName,
        appType,
        instanceId,
        constructedClientId
      });
    } else {
      console.warn('Missing appType or instanceId for constructing clientId', {
        appType,
        instanceId
      });
    }
  }, [appType, instanceId, companyName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }
    if (!realm) {
      setError('Realm is required');
      return;
    }
    if (!clientId) {
      setError('Client ID could not be determined');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const url = 'https://keycloak.hulmsolutions.com/api/keycloak/login';
      // const url = 'http://127.0.0.1:8085/api/keycloak/login';
      
      const payload = {
        realm,
        username: username.trim(),
        password,
        clientId
      };
      console.log('Login request payload:', payload);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      // Handle non-OK responses
      if (!response.ok) {
        const errorText = await response.text();
        // Try to parse as JSON to get the error message
        try {
          const errorData = JSON.parse(errorText);
          // Check for 401 Unauthorized or specific error message
          if (response.status === 401 ||
            errorData.message?.includes('HTTP 401 Unauthorized') ||
            errorData.message?.includes('Login failed')) {
            throw new Error('Incorrect username or password');
          }
          // Use the message from the response if available
          throw new Error(errorData.message || `Server error: ${response.status}`);
        } catch (parseError) {
          // If it's already our custom error, re-throw it
          if (parseError.message === 'Incorrect username or password') {
            throw parseError;
          }
          // Otherwise check status code
          if (response.status === 401) {
            throw new Error('Incorrect username or password');
          }
          // Generic error for other cases
          throw new Error(errorText || `Server responded with status: ${response.status}`);
        }
      }
      const data = await response.json();
      console.log('Login response:', data);

      // Check if the response indicates failure even with 200 status
      if (data.success === false) {
        if (data.message?.includes('HTTP 401 Unauthorized') ||
          data.message?.includes('Login failed')) {
          throw new Error('Incorrect username or password');
        }
        throw new Error(data.message || 'Login failed');
      }

      // ✅ NORMALIZE RESPONSE: Transform new API structure to legacy format
      let normalizedData = { ...data };

      if (data.user) {
        const rawUser = data.user;

        // 1. Flatten clientRoles object into client_roles array
        let clientRolesList = [];
        if (rawUser.clientRoles && typeof rawUser.clientRoles === 'object' && !Array.isArray(rawUser.clientRoles)) {
          // New format: { "HPOS-xxx": ["Admin"], "account": ["manage-account"] }
          Object.values(rawUser.clientRoles).forEach(roles => {
            if (Array.isArray(roles)) {
              clientRolesList = [...clientRolesList, ...roles];
            }
          });
        } else if (Array.isArray(rawUser.client_roles)) {
          // Old format: already an array
          clientRolesList = rawUser.client_roles;
        }

        // 2. Transform camelCase to snake_case and add missing fields
        const normalizedUser = {
          ...rawUser,
          user_id: rawUser.id || rawUser.user_id,
          client_roles: clientRolesList,
          realm_roles: rawUser.realmRoles || rawUser.realm_roles || [],
          given_name: rawUser.firstName || rawUser.given_name,
          family_name: rawUser.lastName || rawUser.family_name,
          email_verified: rawUser.emailVerified !== undefined ? rawUser.emailVerified : rawUser.email_verified,
          name: rawUser.name || `${rawUser.firstName || rawUser.given_name || ''} ${rawUser.lastName || rawUser.family_name || ''}`.trim(),
        };

        normalizedData.user = normalizedUser;
        console.log('Normalized user data:', normalizedUser);
      }

      if (normalizedData) {
        const loginData = {
          username: username.trim(),
          realm,
          clientId,
          responseData: normalizedData
        };

        // Dispatch to Redux store
        if (Store && Store.dispatch) {
          Store.dispatch({
            type: 'LOGIN_SUCCESS',
            payload: loginData
          });

          if (normalizedData.access_token || normalizedData.token) {
            Store.dispatch({
              type: 'GET_TOKEN',
              token: normalizedData.access_token || normalizedData.token
            });
          }

          if (normalizedData.user && normalizedData.user.client_roles && normalizedData.user.client_roles.length > 0) {
            Store.dispatch({
              type: 'SET_USER_ROLE',
              userRole: normalizedData.user.client_roles[0]
            });
          }
        }
        setSuccess(true);
        onLoginSuccess(loginData);
        showSuccessToast('Login successful!');
      } else {
        throw new Error('Invalid login response');
      }
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error.message || 'Login failed. Please try again.';
      setError(errorMessage);
      // Show appropriate toast message
      if (errorMessage === 'Incorrect username or password') {
        showErrorToast('Incorrect username or password. Please try again.');
      } else {
        showErrorToast('Login failed. Please check your credentials.');
      }
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };
  const formatErrorMessage = (errorMsg) => {
    try {
      if (errorMsg.includes('"success":false') || errorMsg.includes('"message"')) {
        const parsedError = JSON.parse(errorMsg);
        return parsedError.message || errorMsg;
      }
      return errorMsg;
    } catch (e) {
      return errorMsg;
    }
  };
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <LoginContainerBox>
      <GlobalStyles styles={{
        body: {
          margin: 0,
          padding: 0,
          overflow: 'hidden',
          backgroundColor: '#ffffff !important',
        }
      }} />
      <GenericToaster />
      <Container maxWidth="sm" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Logo and Title */}
        <LogoContainer>
          <IconBox>
            <PersonIcon sx={{ color: 'white', fontSize: 28 }} />
          </IconBox>
          <Typography
            variant="h4"
            sx={{
              fontSize: '1.75rem',
              fontWeight: 700,
              color: colors.palette.text.primary,
              marginBottom: 1.5,
              textAlign: 'center',
            }}
          >
            Hulm Login
          </Typography>
          <Typography
            sx={{
              fontSize: '1rem',
              color: colors.palette.text.secondary,
              fontWeight: 400,
              textAlign: 'center',
            }}
          >
            Access Your Account
          </Typography>
        </LogoContainer>

        {/* Login Card */}
        <LoginCard>
          <StyledCardContent>
            {/* Success Message */}
            {success && (
              <SuccessMessageData>
                <CheckCircle style={{ marginRight: 8, fontSize: 18 }} />
                <span>Login successful! Redirecting...</span>
              </SuccessMessageData>
            )}

            {/* Error Message */}
            {error && (
              <ErrorContainerData>
                <ErrorIcon sx={{
                  color: colors.palette.status.error,
                  marginRight: 1,
                  fontSize: 18,
                }} />
                <span>{formatErrorMessage(error)}</span>
              </ErrorContainerData>
            )}

            <Form onSubmit={handleSubmit}>
              {/* Username Field */}
              <Box>
                <FieldLabel>
                  Username
                </FieldLabel>
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  id="username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your username"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#333333', marginLeft: '12px' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Password Field */}
              <Box>
                <FieldLabel>
                  Password
                </FieldLabel>
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: '#333333', marginLeft: '12px' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Login Button */}
              <LoginButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || success}
              >
                {loading ? (
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <CircularProgress size={20} color="inherit" style={{ marginRight: 8 }} />
                    <span>Signing In...</span>
                  </Box>
                ) : (
                  'Sign In'
                )}
              </LoginButton>
            </Form>
          </StyledCardContent>
        </LoginCard>

        {/* Footer */}
        <Footer>
          <Typography sx={{ color: colors.palette.text.secondary, fontSize: '0.75rem' }}>
            All rights reserved to Hulm Solutions
          </Typography>
        </Footer>
      </Container>
    </LoginContainerBox>
  );
};

export default CustomLogin;
